import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type {
  HttpResponse,
  TicketCategoryRequest,
  TicketCategoryTreeDto,
  TicketCategoryTreeNodeDto,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import {
  cleanTreeFromParents,
  findNodeInTree,
  populateTreeWithParents,
  recursivelyDisableNode,
  recursivelyEnableNode,
} from "@/helpers/ticket-category-tree";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

export function useAddTicketCategory() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: (body: TicketCategoryRequest) => api.postTicketCategoriesV1(body),
    onSuccess: () => {
      showFlashToast({
        variant: "success",
        message: t("page.repair-category.create-or-edit.create-mode.success-msg"),
      });

      return queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKET_CATEGORIES(projectId) });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.repair-category.create-or-edit.error-msg"),
      });
    },
  });
}

export function useEditTicketCategory() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TicketCategoryRequest }) => api.putTicketCategoriesV1(id, body),
    onSuccess: () => {
      showFlashToast({
        variant: "success",
        message: t("page.repair-category.create-or-edit.edit-mode.success-msg"),
      });

      return queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKET_CATEGORIES(projectId) });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.repair-category.create-or-edit.error-msg"),
      });
    },
  });
}

export function useEditTicketCategoryNode() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: ({ categoryId, node }: { categoryId: string; node: TicketCategoryTreeNodeDto }) =>
      api.putTicketCategoryTreeV1(categoryId, node.id, node),
    onMutate: ({ categoryId, node }) => {
      queryClient.setQueryData<HttpResponse<TicketCategoryTreeDto>>(
        QUERY_KEYS.TICKET_CATEGORY_TREE(projectId, categoryId),
        (oldData) => {
          if (oldData) {
            const clonedData = structuredClone(oldData.data);
            const foundNode = findNodeInTree(clonedData.nodes, node.id);
            if (foundNode) {
              foundNode.locationType = node.locationType;
              foundNode.useTreeOverrideExternalCode = node.useTreeOverrideExternalCode;

              return { ...oldData, data: clonedData };
            }
          }

          return oldData;
        },
      );
    },
    onError: () => {
      showFlashToast({
        message: t("page.ticket-category-tree.enable.error"),
        variant: "alert",
      });
    },
  });
}

export function useEnableTicketCategoryNode() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: ({ categoryId, node }: { categoryId: string; node: TicketCategoryTreeNodeDto }) =>
      api.putTicketCategoryTreeEnableV1(categoryId, node.id),
    onMutate: ({ categoryId, node }) => {
      queryClient.setQueryData<HttpResponse<TicketCategoryTreeDto>>(
        QUERY_KEYS.TICKET_CATEGORY_TREE(projectId, categoryId),
        (oldData) => {
          if (oldData) {
            const clonedData = structuredClone(oldData.data);
            populateTreeWithParents(clonedData.nodes);
            const nodeToEnable = findNodeInTree(clonedData.nodes, node.id);
            if (nodeToEnable) {
              recursivelyEnableNode(nodeToEnable);
            }

            // React-query can't handle recursive structures
            cleanTreeFromParents(clonedData.nodes);

            return { ...oldData, data: clonedData };
          }

          return oldData;
        },
      );
    },
    onError: () => {
      showFlashToast({
        message: t("page.ticket-category-tree.enable.error"),
        variant: "alert",
      });
    },
  });
}

export function useDisableTicketCategoryNode() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: ({ categoryId, node }: { categoryId: string; node: TicketCategoryTreeNodeDto }) =>
      api.putTicketCategoryTreeDisableV1(categoryId, node.id),
    onMutate: ({ categoryId, node }) => {
      queryClient.setQueryData<HttpResponse<TicketCategoryTreeDto>>(
        QUERY_KEYS.TICKET_CATEGORY_TREE(projectId, categoryId),
        (oldData) => {
          if (oldData) {
            const clonedData = structuredClone(oldData.data);
            populateTreeWithParents(clonedData.nodes);
            const nodeToEnable = findNodeInTree(clonedData.nodes, node.id);
            if (nodeToEnable) {
              recursivelyDisableNode(nodeToEnable);
            }

            // React-query can't handle recursive structures
            cleanTreeFromParents(clonedData.nodes);

            return { ...oldData, data: clonedData };
          }

          return oldData;
        },
      );
    },
    onError: () => {
      showFlashToast({
        message: t("page.ticket-category-tree.disable.error"),
        variant: "alert",
      });
    },
  });
}

export function useDeleteTicketCategory() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: (id: string) => api.deleteTicketCategoriesByIdV1(id),
    onSuccess: () => {
      showFlashToast({
        variant: "success",
        message: t("model.repair-category.action.delete.notification.success"),
      });

      return queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TICKET_CATEGORIES(projectId),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("model.repair-category.action.delete.notification.error"),
      });
    },
  });
}
